import React from "react";
import { Formik, Form as FormikForm } from "formik";
import { Button, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaWhatsapp,
  FaEnvelope,
  FaPhoneAlt,
  FaUser,
  FaPaperPlane,
} from "react-icons/fa";
import { contactApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import FormField from "../components/forms/FormField";
import { contactSchema } from "../validation/schemas";
import "./Contact.css";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";
import { localBusinessSchema } from "../utils/seoSchema";

const Contact = () => {
  const { user } = useAuth();

  const initialValues = {
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    message: "",
  };

  return (
    <main className="contact-page">
      <Seo {...PAGE_SEO.contact} jsonLd={localBusinessSchema()} />
      <section className="contact-hero">
        <span className="contact-badge">Get In Touch</span>
        <h1>Contact SG Office</h1>
        <p>
          Reach us through WhatsApp, email enquiry, or a quick contact form.
          We’re here to help with office, school, and business supply needs.
        </p>
      </section>

      <section className="contact-section">
        <div className="contact-cards">
          <a
            href="https://wa.me/971509348007?text=Hi%20SG%20Office%2C%20I%20would%20like%20to%20enquire%20about%20your%20products."
            target="_blank"
            rel="noreferrer"
            className="contact-card whatsapp-card"
          >
            <div className="contact-icon">
              <FaWhatsapp />
            </div>
            <h3>WhatsApp Us</h3>
            <p>Quick response for product enquiries and orders.</p>
            <span>Chat Now</span>
          </a>

          <a
            href={`mailto:sales@sgoffice.me?subject=${encodeURIComponent(
              "Product Enquiry - SG Office"
            )}&body=${encodeURIComponent(
              `Hello SG Office Team,

I would like to enquire about your products/services.

Name:
Company:
Email:
Phone:
Product/Service Required:

Message:

Thank you.`
            )}`}
            className="contact-card email-card"
          >
            <div className="contact-icon">
              <FaEnvelope />
            </div>
            <h3>Email Enquiry</h3>
            <p>Send your requirements directly to our sales team.</p>
            <span>sales@sgoffice.me</span>
          </a>

          <a href="tel:0509348007" className="contact-card phone-card">
            <div className="contact-icon">
              <FaPhoneAlt />
            </div>
            <h3>Call Us</h3>
            <p>Speak with us for quick assistance and order support.</p>
            <span>0509348007</span>
          </a>
        </div>

        <div className="contact-form-wrapper">
          <div className="form-info">
            <span className="contact-badge">Quick Enquiry</span>
            <h2>Send Us a Message</h2>
            <p>
              Fill in your details and our team will get back to you as soon as
              possible.
            </p>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={contactSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                await contactApi.create(values);
                toast.success("Thank you! Your enquiry has been submitted.");
                resetForm({
                  values: {
                    name: user?.name || "",
                    email: user?.email || "",
                    phone: user?.phone || "",
                    message: "",
                  },
                });
              } catch (error) {
                toast.error(getErrorMessage(error, "Could not submit enquiry"));
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <FormikForm className="contact-form" noValidate>
                <FormField
                  name="name"
                  label="Full Name"
                  labelIcon={<FaUser />}
                  placeholder="Enter your name"
                />

                <Row>
                  <Col md={6}>
                    <FormField
                      name="email"
                      type="email"
                      label="Email"
                      labelIcon={<FaEnvelope />}
                      placeholder="Enter your email"
                    />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="phone"
                      type="tel"
                      label="Phone"
                      labelIcon={<FaPhoneAlt />}
                      placeholder="Enter phone number"
                    />
                  </Col>
                </Row>

                <FormField
                  name="message"
                  type="textarea"
                  rows={5}
                  label="Message"
                  placeholder="Tell us your requirement..."
                />

                <Button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  <FaPaperPlane />{" "}
                  {isSubmitting ? "Sending..." : "Send Enquiry"}
                </Button>
              </FormikForm>
            )}
          </Formik>
        </div>
      </section>
    </main>
  );
};

export default Contact;
